import React, { useState, useEffect, useRef } from 'react';
import { Modal } from '../ui/Modal';
import { useApp } from '../../context/AppContext';

import { eachDayOfInterval, parseISO, isBefore, format, getDay } from 'date-fns';
import apiService from '../../services/api';
import toast from 'react-hot-toast';
import {
  Umbrella, Home, Heart, Baby, DollarSign, Thermometer,
  Tag, Cake, Users, Stethoscope, GraduationCap,
  Briefcase, Globe, RefreshCw, Gift, ChevronDown, Check,
  AlertTriangle, Loader2,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────
// Schedule helpers
// ─────────────────────────────────────────────────────────────
const DAY_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

/**
 * workDays: boolean[7] where index = JS getDay() value.
 * e.g. [false, true, true, false, true, true, false]
 *      Sun=off  Mon    Tue   Wed=off Thu   Fri   Sat=off
 * Returns count of days in range that are true in workDays.
 */
function countWorkingDays(fromStr, toStr, workDays) {
  if (!fromStr || !toStr) return 0;
  const start = parseISO(fromStr);
  const end   = parseISO(toStr);
  if (isBefore(end, start)) return 0;
  // EmployeeSchedule.workDays is Mon-first: [Mon, Tue, Wed, Thu, Fri, Sat, Sun]
  // JS getDay() is Sun-first: 0=Sun, 1=Mon ... 6=Sat
  // Remap: scheduleIndex = (jsDay + 6) % 7
  // Default Mon–Fri only if no schedule
  const schedule = workDays || [true,true,true,true,true,false,false];
  return eachDayOfInterval({ start, end })
    .filter(day => schedule[(getDay(day) + 6) % 7] === true)
    .length;
}

/** Returns day names (e.g. ['Wednesday']) that are false in workDays, Mon–Fri only */
function getNonWorkingDayNames(workDays) {
  if (!workDays) return [];
  // workDays is Mon-first: index 0=Mon, 1=Tue, 2=Wed, 3=Thu, 4=Fri
  const MON_FRI_NAMES = ['Monday','Tuesday','Wednesday','Thursday','Friday'];
  return [0,1,2,3,4]
    .filter(i => workDays[i] === false)
    .map(i => MON_FRI_NAMES[i]);
}

// ─────────────────────────────────────────────────────────────
// Half-day / hours constants
// ─────────────────────────────────────────────────────────────
const AM_HOURS = 3;    // 09:00 – 12:00
const PM_HOURS = 4.5;  // 12:00 – 17:30

const WORK_START = '09:00';
const WORK_END   = '17:30';

// Generate 15-min interval time options between 09:00 – 17:30
function generateTimeOptions() {
  const options = [];
  let h = 9, m = 0;
  while (h < 17 || (h === 17 && m <= 30)) {
    const hh = String(h).padStart(2, '0');
    const mm = String(m).padStart(2, '0');
    options.push(`${hh}:${mm}`);
    m += 15;
    if (m >= 60) { m = 0; h++; }
  }
  return options;
}
const TIME_OPTIONS = generateTimeOptions();

function parseHHMM(str) {
  if (!str) return null;
  const [h, m] = str.split(':').map(Number);
  return h * 60 + m;
}

function calcCustomHours(start, end) {
  const s = parseHHMM(start);
  const e = parseHHMM(end);
  if (s == null || e == null || e <= s) return 0;
  return Math.round(((e - s) / 60) * 100) / 100;
}

const PERIOD_OPTIONS = [
  { value: 'full',   label: 'Full day',      sublabel: '09:00 – 17:30',               emoji: '📅' },
  { value: 'AM',     label: 'Morning',       sublabel: `09:00 – 12:00 · ${AM_HOURS}h`, emoji: '🌅' },
  { value: 'PM',     label: 'Afternoon',     sublabel: `01:00 – 17:30 · ${PM_HOURS}h`, emoji: '🌇' },
  { value: 'custom', label: 'Custom hours',  sublabel: 'Pick start & end',             emoji: '🕐' },
];

const ICON_MAP = {
  'Annual Leave':            Umbrella,
  'Holiday':                 Umbrella,
  'Sick Leave':              Thermometer,
  'Sick-Deductable':         Thermometer,
  'Remote Work':             Home,
  'Working Remotely':        Home,
  'Working Remotely Abroad': Globe,
  'Unpaid Leave':            DollarSign,
  'Compassionate Leave':     Heart,
  'Compassionate':           Heart,
  'Parental Leave':          Baby,
  'Maternity':               Baby,
  'Paternity':               Baby,
  'Medical':                 Stethoscope,
  'Meeting':                 Users,
  'Training':                GraduationCap,
  'Birthday':                Cake,
  'Christmas':               Gift,
  'Day Swap-Working':        RefreshCw,
  'Day Swap- Not Working':   RefreshCw,
  'Di Working':              Briefcase,
};

function getIcon(name) {
  if (ICON_MAP[name]) return ICON_MAP[name];
  const match = Object.entries(ICON_MAP).find(([key]) =>
    name?.toLowerCase().includes(key.toLowerCase())
  );
  return match?.[1] || Tag;
}

// ─────────────────────────────────────────────────────────────
// Leave type dropdown
// ─────────────────────────────────────────────────────────────
function LeaveTypeDropdown({ leaveTypes, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const deductible    = leaveTypes.filter(t =>  t.affectsBalance);
  const nonDeductible = leaveTypes.filter(t => !t.affectsBalance);
  const selected      = leaveTypes.find(t => t.name === value);
  const SelectedIcon  = selected ? getIcon(selected.name) : Tag;

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function renderItem(type) {
    const isSelected = type.name === value;
    const Icon       = getIcon(type.name);
    return (
      <div
        key={type.id}
        onClick={() => { onChange(type.name); setOpen(false); }}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', cursor: 'pointer', background: isSelected ? 'var(--accent)' : 'transparent', transition: 'background 0.1s' }}
        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--hover)'; }}
        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
      >
        <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: isSelected ? 'var(--on-accent-soft)' : (type.color || '#ffc81e'), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={14} style={{ color: isSelected ? 'var(--on-accent)' : '#1C1A14' }} strokeWidth={2.5} />
        </div>
        <span style={{ fontSize: 13, fontWeight: 500, color: isSelected ? 'var(--on-accent)' : 'var(--text)', flex: 1 }}>{type.name}</span>
        {isSelected && <Check size={14} style={{ color: 'var(--on-accent)', flexShrink: 0 }} />}
      </div>
    );
  }

  function renderGroup(label, types) {
    if (!types.length) return null;
    return (
      <>
        <div style={{ padding: '8px 14px 4px', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
        {types.map(t => renderItem(t))}
      </>
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', border: `1.5px solid ${open ? 'var(--accent)' : 'var(--field-border)'}`, borderRadius: 12, background: 'var(--field)', cursor: 'pointer', outline: 'none', transition: 'border-color 0.15s' }}
      >
        {selected ? (
          <>
            <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: selected.color || '#ffc81e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SelectedIcon size={14} color="#1C1A14" strokeWidth={2.5} />
            </div>
            <span style={{ flex: 1, textAlign: 'left', fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{selected.name}</span>
          </>
        ) : (
          <span style={{ flex: 1, textAlign: 'left', fontSize: 13, color: 'var(--text-muted)' }}>Select leave type…</span>
        )}
        <ChevronDown size={15} style={{ color: 'var(--text-muted)', flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>

      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 200, background: 'var(--menu-bg)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: 'var(--menu-shadow)', maxHeight: 320, overflowY: 'auto' }}>
          {renderGroup('Deductible', deductible)}
          {deductible.length > 0 && nonDeductible.length > 0 && <div style={{ borderTop: '1px solid var(--border-soft)', margin: '4px 0' }} />}
          {renderGroup('Non-deductible', nonDeductible)}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Time picker dropdown
// ─────────────────────────────────────────────────────────────
function TimePicker({ label, value, onChange, minTime, maxTime }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const listRef = useRef(null);

  const filtered = TIME_OPTIONS.filter(t => {
    const m = parseHHMM(t);
    if (minTime && m <= parseHHMM(minTime)) return false;
    if (maxTime && m >= parseHHMM(maxTime)) return false;
    return true;
  });

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Scroll selected item into view when dropdown opens
  useEffect(() => {
    if (open && listRef.current && value) {
      const el = listRef.current.querySelector('[data-selected="true"]');
      if (el) el.scrollIntoView({ block: 'center' });
    }
  }, [open, value]);

  return (
    <div ref={ref} style={{ position: 'relative', flex: 1 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '9px 12px', border: `1.5px solid ${open ? 'var(--accent)' : 'var(--field-border)'}`,
          borderRadius: 12, background: 'var(--field)', cursor: 'pointer', outline: 'none',
          fontSize: 14, fontWeight: 600, color: value ? 'var(--text)' : 'var(--text-muted)',
          transition: 'border-color 0.15s',
        }}
      >
        <span>{value || '-- : --'}</span>
        <ChevronDown size={14} style={{ color: 'var(--text-muted)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>

      {open && (
        <div
          ref={listRef}
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 300,
            background: 'var(--menu-bg)', border: '1px solid var(--border)', borderRadius: 12,
            boxShadow: 'var(--menu-shadow)', maxHeight: 200, overflowY: 'auto',
          }}
        >
          {filtered.length === 0 && (
            <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>No valid times</div>
          )}
          {filtered.map(t => {
            const isSelected = t === value;
            return (
              <div
                key={t}
                data-selected={isSelected}
                onClick={() => { onChange(t); setOpen(false); }}
                style={{
                  padding: '8px 14px', cursor: 'pointer', fontSize: 13, fontWeight: isSelected ? 700 : 500,
                  color: isSelected ? 'var(--on-accent)' : 'var(--text)',
                  background: isSelected ? 'var(--accent)' : 'transparent',
                  transition: 'background 0.1s',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--hover)'; }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
              >
                {t}
                {isSelected && <Check size={13} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Custom hours picker — start + end time with calculated hours
// ─────────────────────────────────────────────────────────────
function CustomHoursPicker({ startTime, endTime, onStartChange, onEndChange }) {
  const hours = calcCustomHours(startTime, endTime);
  const valid = hours > 0;

  return (
    <div>
      <div style={{ display: 'flex', gap: 10 }}>
        <TimePicker
          label="Start time"
          value={startTime}
          onChange={onStartChange}
          maxTime={WORK_END}
        />
        <TimePicker
          label="End time"
          value={endTime}
          onChange={onEndChange}
          minTime={startTime || WORK_START}
          maxTime={WORK_END}
        />
      </div>

      {/* Calculated hours display */}
      <div style={{
        marginTop: 8, display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px', borderRadius: 8,
        background: valid ? 'var(--ok-bg)' : 'var(--surface-2)',
        border: `1px solid ${valid ? 'var(--ok-border)' : 'var(--border)'}`,
        transition: 'all 0.2s',
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: valid ? 'var(--ok-text)' : 'var(--text-muted)' }} strokeWidth="2" strokeLinecap="round">
          <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
        </svg>
        {valid ? (
          <>
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ok-text)', flex: 1 }}>
              {startTime} – {endTime}
            </span>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 20,
              background: 'var(--ok-bg)', color: 'var(--ok-text)', border: '1px solid var(--ok-border)',
            }}>
              {hours}h total
            </span>
          </>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {startTime && endTime && !valid
              ? 'End time must be after start time'
              : 'Select start and end time to calculate hours'}
          </span>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Period selector — Full / AM / PM / Custom toggle
// ─────────────────────────────────────────────────────────────
function PeriodSelector({ value, onChange }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Duration
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
        {PERIOD_OPTIONS.map(opt => {
          const isSelected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              style={{
                padding: '10px 4px', borderRadius: 12,
                border: isSelected ? '2px solid var(--accent)' : '1px solid var(--field-border)',
                background: isSelected ? 'var(--tint)' : 'var(--field)',
                cursor: 'pointer', textAlign: 'center',
                transition: 'all 0.15s', outline: 'none',
              }}
            >
              <div style={{ fontSize: 15, marginBottom: 3 }}>{opt.emoji}</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: isSelected ? 'var(--accent)' : 'var(--text)', marginBottom: 2 }}>
                {opt.label}
              </div>
              <div style={{ fontSize: 9.5, color: isSelected ? 'var(--accent)' : 'var(--text-muted)', lineHeight: 1.3 }}>
                {opt.sublabel}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Duration summary pill
// ─────────────────────────────────────────────────────────────
function DurationPill({ days, period, affectsBalance, customHours, startTime, endTime, nonWorkingDays }) {
  const clockIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
  );

  const isCustom = period === 'custom';
  const customValid = isCustom && customHours > 0;

  if (!affectsBalance) {
    const label =
      period === 'AM'     ? `🌅 Morning half-day · ${AM_HOURS}h (09:00–12:00)` :
      period === 'PM'     ? `🌇 Afternoon half-day · ${PM_HOURS}h (12:00–17:30)` :
      isCustom && customValid ? `🕐 ${startTime} – ${endTime} · ${customHours}h` :
      days > 0            ? `${days} working day${days !== 1 ? 's' : ''}` : null;
    if (!label) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-2)', fontSize: 12, fontWeight: 500, padding: '8px 12px', borderRadius: 8 }}>
        {clockIcon}
        <span>{label}</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: 'var(--border-soft)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
          No balance impact
        </span>
      </div>
    );
  }

  if (period === 'AM') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--tint)', color: 'var(--accent)', fontSize: 12, fontWeight: 500, padding: '8px 12px', borderRadius: 10 }}>
        {clockIcon}
        🌅 Morning half-day · {AM_HOURS}h (09:00–12:00)
        <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'var(--tint-strong)', color: 'var(--accent)' }}>−½ day</span>
      </div>
    );
  }
  if (period === 'PM') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--tint)', color: 'var(--accent)', fontSize: 12, fontWeight: 500, padding: '8px 12px', borderRadius: 10 }}>
        {clockIcon}
        🌇 Afternoon half-day · {PM_HOURS}h (12:00–17:30)
        <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'var(--tint-strong)', color: 'var(--accent)' }}>−½ day</span>
      </div>
    );
  }
  if (isCustom && customValid) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-2)', fontSize: 12, fontWeight: 500, padding: '8px 12px', borderRadius: 8 }}>
        {clockIcon}
        🕐 {startTime} – {endTime} · {customHours}h
        <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: 'var(--border-soft)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
          No balance impact
        </span>
      </div>
    );
  }
  if (days > 0) {
    const excludedNames = (nonWorkingDays || []).map(d => d.charAt(0).toUpperCase() + d.slice(1));
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--tint)', color: 'var(--accent)', fontSize: 12, fontWeight: 500, padding: '8px 12px', borderRadius: 10 }}>
          {clockIcon}
          {days} working day{days !== 1 ? 's' : ''} requested
          <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'var(--tint-strong)', color: 'var(--accent)' }}>−{days}d</span>
        </div>
        {excludedNames.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--warn-text)', background: 'var(--warn-bg-soft)', border: '1px solid var(--warn-border)', borderRadius: 6, padding: '5px 10px' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: 'var(--warn-text)', flexShrink: 0 }} strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {excludedNames.join(', ')} excluded — not part of your working schedule
          </div>
        )}
      </div>
    );
  }
  return null;
}

// ─────────────────────────────────────────────────────────────
// RequestForm
// ─────────────────────────────────────────────────────────────
export function RequestForm({ onClose, prefill = null, onRequestCreated }) {
  const { currentUser, employees } = useApp();

  const [leaveTypes,   setLeaveTypes]   = useState([]);
  const [workDays,     setWorkDays]     = useState(null); // boolean[7] from EmployeeSchedule
  const [form,         setForm]         = useState({
    type:      '',
    from:      '',
    to:        '',
    note:      '',
    period:    'full',
    startTime: '',
    endTime:   '',
  });
  const [error,        setError]        = useState('');
  const [submitting,   setSubmitting]   = useState(false);
  const [submitted,    setSubmitted]    = useState(false);
  const [submittedReq, setSubmittedReq] = useState(null);

  const isForOther  = !!prefill?.empName && prefill?.empId !== currentUser?.id;
  const isHalfDay   = form.period === 'AM' || form.period === 'PM';
  const isCustom    = form.period === 'custom';
  const isSingleDay = isHalfDay || isCustom;

  // ─── NEW: South Africa Team members don't have a leave balance tracked at
  // all, so requests submitted for them never deduct — regardless of the
  // selected leave type's own affectsBalance setting. ───
  const targetEmpId    = prefill?.empId ?? currentUser?.id;
  const targetEmp      = employees?.find(e => e.id === targetEmpId);
  const isSouthAfrica  = [
    targetEmp?.dept,
    targetEmp?.department,
    targetEmp?.department?.name,
    targetEmp?.team,
    targetEmp?.team?.name,
    ...(Array.isArray(targetEmp?.departments) ? targetEmp.departments.map(d => d?.departmentName ?? d?.name) : []),
  ].some(v => typeof v === 'string' && v.toLowerCase().includes('south africa'));

  const selectedLeaveType = leaveTypes.find(t => t.name === form.type);
  const affectsBalance    = isSouthAfrica
    ? false
    : (selectedLeaveType ? selectedLeaveType.affectsBalance !== false : true);

  const customHours = isCustom ? calcCustomHours(form.startTime, form.endTime) : 0;

  useEffect(() => {
    apiService.getLeaveTypes()
      .then(res => {
        const raw    = res?.data ?? res ?? [];
        const active = raw.filter(t => t.isActive !== false);
        const sorted = [...active].sort((a, b) => {
          if (a.affectsBalance && !b.affectsBalance) return -1;
          if (!a.affectsBalance && b.affectsBalance) return  1;
          if (a.name === 'Holiday') return -1;
          if (b.name === 'Holiday') return  1;
          return 0;
        });
        setLeaveTypes(sorted);
        setForm(f => ({
          ...f,
          type:   sorted[0]?.name || '',
          from:   prefill?.from   ?? '',
          to:     prefill?.to     ?? '',
          period: prefill?.halfDayPeriod ?? prefill?.period ?? 'full',
        }));
      })
      .catch(() => {});

    // Fetch the employee's schedules and pick the most recent effective one
    const scheduleEmpId = prefill?.empId ?? currentUser?.id;
    if (scheduleEmpId) {
      apiService.getEmployeeSchedules(scheduleEmpId)
        .then(res => {
          // axios interceptor returns response.data directly, so res = {success, data:[]}
          // but some endpoints wrap further, handle both shapes
          const raw  = res?.data ?? res;
          const list = Array.isArray(raw) ? raw : Array.isArray(raw?.schedules) ? raw.schedules : [];
          if (list.length === 0) return;
          // Sort descending by effectiveFrom, take the most recent active schedule
          // API returns 'from' as the date field (not 'effectiveFrom')
          const latest = [...list].sort((a, b) =>
            new Date(b.from ?? b.effectiveFrom) - new Date(a.from ?? a.effectiveFrom)
          )[0];
          // API returns { id, from, days: boolean[7], hoursPerDay } — field is 'days' not 'workDays'
          const wd = latest?.workDays ?? latest?.days;
          if (Array.isArray(wd) && wd.length === 7) {
            setWorkDays(wd);
          }
        })
        .catch(err => console.warn('[RequestForm] schedule fetch failed:', err));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handlePeriodChange(newPeriod) {
    setError('');
    setForm(prev => ({
      ...prev,
      period:    newPeriod,
      startTime: newPeriod === 'custom' ? (prev.startTime || WORK_START) : prev.startTime,
      endTime:   newPeriod === 'custom' ? prev.endTime : prev.endTime,
      to: (newPeriod === 'AM' || newPeriod === 'PM' || newPeriod === 'custom')
        ? (prev.from || prev.to)
        : prev.to,
    }));
  }

  function handleFromChange(val) {
    setError('');
    setForm(prev => ({
      ...prev,
      from: val,
      to:   isSingleDay ? val : prev.to,
    }));
  }

  // Derive excluded day names for display (Mon–Fri only)
  const nonWorkingDayNames = getNonWorkingDayNames(workDays);

  const days = form.from && form.to && !isSingleDay
    ? countWorkingDays(form.from, form.to, workDays)
    : 0;

  const set = (k, v) => { setError(''); setForm(p => ({ ...p, [k]: v })); };

  const validate = () => {
    if (!form.type) return 'Please select a leave type.';
    if (!form.from) return 'Please select a date.';
    if (isCustom) {
      if (!form.startTime) return 'Please select a start time.';
      if (!form.endTime)   return 'Please select an end time.';
      if (customHours <= 0) return 'End time must be after start time.';
    } else if (!isHalfDay) {
      if (!form.to) return 'Please select an end date.';
      if (isBefore(parseISO(form.to), parseISO(form.from))) return 'End date must be after start date.';
      if (days > 15) return 'Maximum consecutive leave is 15 working days.';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        to:        isSingleDay ? form.from : form.to,
        days:      isHalfDay || isCustom ? null : days,
        hours:     isHalfDay
                     ? (form.period === 'AM' ? AM_HOURS : PM_HOURS)
                     : isCustom ? customHours : null,
        startTime: isCustom ? form.startTime : null,
        endTime:   isCustom ? form.endTime   : null,
        // ─── South Africa Team requests never deduct from a balance,
        // in addition to the existing custom-hours exception ───
        affectsBalance: (isCustom || isSouthAfrica) ? false : undefined,
        workDays: workDays,          // boolean[7]; backend recomputes days server-side
        empId:     prefill?.empId ?? currentUser.id,
      };
      const res     = await apiService.createLeaveRequest(payload);
      const created = res?.data ?? res;

      const resolvedLeaveType = leaveTypes.find(t => t.name === (created.type || form.type));
      const enriched = {
        ...created,
        type:      created.type      || form.type,
        period:    created.period    || form.period,
        leaveType: created.leaveType || resolvedLeaveType || null,
      };

      onRequestCreated?.(enriched, null);

      setSubmittedReq({
        id:            enriched.id,
        type:          form.type,
        from:          form.from,
        to:            isSingleDay ? form.from : form.to,
        days,
        period:        form.period,
        affectsBalance: (isCustom || isSouthAfrica) ? false : affectsBalance,
        customHours:   isCustom ? customHours : 0,
        startTime:     form.startTime,
        endTime:       form.endTime,
        // ─── NEW: real status from the API (e.g. South Africa Team requests
        // come back 'approved' instead of 'pending') — the success screen
        // reads this instead of always assuming pending. ───
        status:        created?.status || 'pending',
      });
      setSubmitted(true);
    } catch (apiErr) {
      const msg = apiErr?.response?.data?.message || 'Failed to submit request. Please try again.';
      setError(msg);
      onRequestCreated?.(null, apiErr);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success screen ────────────────────────────────────────
  if (submitted && submittedReq) {
    const LeaveIcon = getIcon(submittedReq.type);
    const leaveType = leaveTypes.find(t => t.name === submittedReq.type);
    const iconColor = leaveType?.color || '#ffc81e';
    const isHalfDaySubmitted = submittedReq.period === 'AM' || submittedReq.period === 'PM';
    const isCustomSubmitted  = submittedReq.period === 'custom';
    const periodOpt = PERIOD_OPTIONS.find(p => p.value === submittedReq.period);
    // ─── NEW: branch copy/badge on real status instead of hardcoding "pending" ───
    const isApproved = submittedReq.status === 'approved';

    return (
      <Modal title="Request Submitted" onClose={onClose}>
        <div style={{ textAlign: 'center', paddingTop: 8 }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--ok-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: 'var(--ok-text)' }} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
            {isApproved ? 'Request approved!' : 'Request submitted!'}
          </h3>
          <p style={{ margin: '0 0 20px', fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6 }}>
            {isApproved
              ? (isForOther ? `${prefill.empName}'s request has been approved.` : 'Your request has been approved.')
              : (isForOther ? `${prefill.empName}'s request is pending approval.` : 'Your request is pending approval.')
            }
          </p>

          <div style={{ background: 'var(--surface-2)', borderRadius: 12, border: '1px solid var(--border)', padding: '14px 16px', marginBottom: 20, textAlign: 'left' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, flexShrink: 0, background: iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <LeaveIcon size={16} color="#1C1A14" strokeWidth={2.5} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{submittedReq.type}</p>
                <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)' }}>
                  {isHalfDaySubmitted
                    ? `${periodOpt?.emoji} ${periodOpt?.label} half-day · ${periodOpt?.sublabel?.split('·')[1]?.trim()}`
                    : isCustomSubmitted
                      ? `🕐 ${submittedReq.startTime} – ${submittedReq.endTime} · ${submittedReq.customHours}h`
                      : `${submittedReq.days} working day${submittedReq.days !== 1 ? 's' : ''}`
                  }
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                {isApproved ? (
                  <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: 'var(--ok-bg)', color: 'var(--ok-text)', border: '1px solid var(--ok-border)' }}>Approved</span>
                ) : (
                  <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: 'var(--warn-bg)', color: 'var(--warn-text)', border: '1px solid var(--warn-border)' }}>Pending</span>
                )}
                {submittedReq.affectsBalance && !isCustomSubmitted ? (
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20, background: 'var(--warn-bg)', color: 'var(--warn-text)', border: '1px solid var(--warn-border)' }}>
                    {isHalfDaySubmitted ? '−½ day' : isCustomSubmitted ? `−${submittedReq.customHours}h` : `−${submittedReq.days}d`}
                  </span>
                ) : (
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20, background: 'var(--border-soft)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
                    No balance impact
                  </span>
                )}
              </div>
            </div>

            {isHalfDaySubmitted && (
              <div style={{ marginBottom: 10, padding: '6px 10px', borderRadius: 6, background: 'var(--tint)', border: '1px solid var(--tint-strong)', fontSize: 12, color: 'var(--accent)', fontWeight: 500 }}>
                {submittedReq.period === 'AM' ? '🌅 Morning: 09:00–12:00' : '🌇 Afternoon: 12:00–17:30'}
              </div>
            )}

            {isCustomSubmitted && (
              <div style={{ marginBottom: 10, padding: '6px 10px', borderRadius: 6, background: 'var(--ok-bg)', border: '1px solid var(--ok-border)', fontSize: 12, color: 'var(--ok-text)', fontWeight: 500 }}>
                🕐 Custom hours: {submittedReq.startTime} – {submittedReq.endTime}
              </div>
            )}

            <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
              <div style={{ flex: 1, padding: '10px 12px', background: 'var(--surface)', borderRight: '1px solid var(--border)' }}>
                <p style={{ margin: 0, fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date</p>
                <p style={{ margin: '3px 0 0', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{format(parseISO(submittedReq.from), 'EEE, dd MMM')}</p>
              </div>
              {!isHalfDaySubmitted && !isCustomSubmitted && (
                <div style={{ flex: 1, padding: '10px 12px', background: 'var(--surface)', borderRight: '1px solid var(--border)' }}>
                  <p style={{ margin: 0, fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>To</p>
                  <p style={{ margin: '3px 0 0', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{format(parseISO(submittedReq.to), 'EEE, dd MMM')}</p>
                </div>
              )}
              {isCustomSubmitted && (
                <div style={{ flex: 1, padding: '10px 12px', background: 'var(--surface)', borderRight: '1px solid var(--border)' }}>
                  <p style={{ margin: 0, fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Time</p>
                  <p style={{ margin: '3px 0 0', fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{submittedReq.startTime}–{submittedReq.endTime}</p>
                </div>
              )}
              <div style={{ flex: 1, padding: '10px 12px', background: 'var(--surface)' }}>
                <p style={{ margin: 0, fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {isCustomSubmitted ? 'Hours' : isHalfDaySubmitted ? 'Hours' : 'Days'}
                </p>
                <p style={{ margin: '3px 0 0', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                  {isCustomSubmitted
                    ? `${submittedReq.customHours}h`
                    : isHalfDaySubmitted
                      ? `${submittedReq.period === 'AM' ? AM_HOURS : PM_HOURS}h`
                      : submittedReq.days
                  }
                </p>
              </div>
            </div>
          </div>

          <button className="btn-primary" onClick={onClose} style={{ width: '100%' }}>Done</button>
        </div>
      </Modal>
    );
  }

  // ── Form ──────────────────────────────────────────────────
  const modalTitle = isForOther ? `New Request — ${prefill.empName}` : 'New Leave Request';

  return (
    <Modal title={modalTitle} onClose={onClose}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">

        {isForOther && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--tint)', borderRadius: 12, border: '1px solid var(--tint-strong)' }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, background: 'var(--avatar-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'var(--avatar-text)' }}>
              {prefill.empName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{prefill.empName}</p>
              <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)' }}>Submitting on behalf of this employee</p>
            </div>
          </div>
        )}

        {/* Leave type */}
        <div>
          <label className="block text-xs font-semibold th-text-2 mb-1.5">Leave Type</label>
          <LeaveTypeDropdown leaveTypes={leaveTypes} value={form.type} onChange={v => set('type', v)} />
          {!affectsBalance && form.type && (
            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-2)', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 10px' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ flexShrink: 0 }} strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {isSouthAfrica
                ? 'South Africa Team leave is not tracked against a balance'
                : 'This leave type does not affect your annual leave balance'}
            </div>
          )}
        </div>

        {/* Period selector */}
        <PeriodSelector value={form.period} onChange={handlePeriodChange} />

        {/* Date input */}
        {isSingleDay ? (
          <div>
            <label className="block text-xs font-semibold th-text-2 mb-1.5">Date</label>
            <input
              type="date"
              className="form-input"
              value={form.from}
              onChange={e => handleFromChange(e.target.value)}
              required
            />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold th-text-2 mb-1.5">Start Date</label>
              <input
                type="date"
                className="form-input"
                value={form.from}
                onChange={e => handleFromChange(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold th-text-2 mb-1.5">End Date</label>
              <input
                type="date"
                className="form-input"
                value={form.to}
                min={form.from}
                onChange={e => set('to', e.target.value)}
                required
              />
            </div>
          </div>
        )}

        {/* Custom time pickers — only shown when period === 'custom' */}
        {isCustom && (
          <CustomHoursPicker
            startTime={form.startTime}
            endTime={form.endTime}
            onStartChange={v => {
              setError('');
              setForm(p => ({ ...p, startTime: v, endTime: p.endTime && parseHHMM(p.endTime) <= parseHHMM(v) ? '' : p.endTime }));
            }}
            onEndChange={v => set('endTime', v)}
          />
        )}

        {/* Duration summary pill */}
        {(isHalfDay || (isCustom && customHours > 0) || days > 0) && form.from && (
          <DurationPill
            days={days}
            period={form.period}
            affectsBalance={affectsBalance}
            customHours={customHours}
            startTime={form.startTime}
            endTime={form.endTime}
            nonWorkingDays={nonWorkingDayNames}
          />
        )}

        {/* Note */}
        <div>
          <label className="block text-xs font-semibold th-text-2 mb-1.5">
            Note <span className="th-muted font-normal">(optional)</span>
          </label>
          <textarea
            className="form-input resize-none"
            rows={3}
            placeholder="Reason or additional details…"
            value={form.note}
            onChange={e => set('note', e.target.value)}
          />
        </div>

        {error && (
          <p className="text-xs px-3 py-2 rounded-[10px]" style={{ color: 'var(--bad-text)', background: 'var(--bad-bg)' }}>{error}</p>
        )}

        <div className="flex gap-3 pt-1">
          <button type="button" className="btn-secondary flex-1" onClick={onClose}>Cancel</button>
          <button
            type="submit"
            className="btn-primary flex-1"
            disabled={submitting}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '10px 14px', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            {submitting ? <><Loader2 size={14} className="animate-spin" /> Submitting…</> : 'Submit Request'}
          </button>
        </div>
      </form>
    </Modal>
  );
}